import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AccordionGroupMeta } from '@cudirect/shared-ui-library';
import { BuildingBlock, BuildingBlockControlBaseComponent } from '../building-block.types';
import { PrimitiveType } from '@workflow/class/workflow.type';
import { Disclosure, DisclosureApplicant } from '@workflow/ecp-workflow/dynamic-app/types/disclosure.type';
import { DisclosuresGroup } from '../disclosure/disclosure.component';

/** component to show available accounts and services */
@BuildingBlock('cross-sell')
@Component({
  selector: 'ecp-crosssell',
  styleUrls: ['./cross-sell.component.scss'],
  templateUrl: './cross-sell.component.html',
  standalone: false
})
export class CrossSellComponent extends BuildingBlockControlBaseComponent implements OnInit, OnDestroy {
  public isOAOFlow = false;
  public disclosure: Disclosure = {} as Disclosure;
  public isPageSubmitted = false;
  public rd_id_prefix = 'cross-sell-opt-in';
  @ViewChild('crossSellGroup') public crossSellGroup: DisclosuresGroup;
  /**
   * Accordion group meta data
   */
  public accordionGroupMeta = new AccordionGroupMeta();
  /** On Init */
  public ngOnInit(): void {
    this.isOAOFlow = this.workflowService?.currentFlow?.id === 'oao' || this.workflowService?.currentFlow?.id.indexOf('oao') !== -1;
    this.onLoadAsync(() => {
      this.disclosure = this.field?.config?.content ? JSON.parse(this.field.config.content.toString()) : {};
    });

    this.updateValidity();
    this.pageSubmitted$.subscribe((value) => {
      this.isPageSubmitted = value;
      if (this.isPageSubmitted) {
        this.setDisclosure();
      }
    });
  }

  // Load disclosure asyn, bcze in some cases fetch disclosures from api call
  protected readonly onLoadAsync = (cb: () => void): void => {
    if (this.field?.config?.onLoad) {
      const config = this.logicService.evaluate<Promise<boolean>>(
        this.field.config.onLoad,
        {
          field: this.field,
          page: this.workflowService.currentPage
        },
        'single'
      );
      if (config && config[0]) {
        config[0].then(cb);
      } else {
        cb();
      }
    }
  };

  /**
   * Focus 1st Invalid disclosure checkbox
   */
  public setDisclosure(): void {
    if (!this.crossSellGroup || !this.disclosure || !this.disclosure.applicants) {
      return;
    }

    if (this.disclosure.applicants.length > 1 && !this.disclosure.applicants[0].accepted) {
      this.disclosure.applicants[1].readOnly = true;
    }

    let firstInvalidId: string;
    this.crossSellGroup.eaAccordions.forEach((element, disIndex) => {
      element.close();
      this.disclosure.applicants.forEach((applicant, applicantIndex) => {
        if (this.isNullOrEmpty(applicant.accepted)) {
          if (disIndex === 0) {
            // Get only the 1st invalid control id, skip other invalid id
            if (!firstInvalidId) {
              firstInvalidId = `${this.rd_id_prefix}-${applicantIndex}`;
            }
            element.open();
          }
        }
      });
    });

    if (firstInvalidId) {
      setTimeout(() => {
        this.focusService.setFocus(firstInvalidId);
      }, 0);
    }
  }

  public updateValidity(): void {
    if (this.allApplicantsAccepted(this.disclosure?.applicants)) {
      this.onChange(true);
    }
  }

  private allApplicantsAccepted(applicants: DisclosureApplicant[]): boolean {
    return (
      applicants && applicants.filter((t: DisclosureApplicant) => !t.disabled && !t.readOnly && this.isNullOrEmpty(t.accepted)).length === 0
    );
  }

  public isNullOrEmpty(accepted): boolean {
    return accepted === null || accepted === undefined;
  }

  protected readonly onChange = (isAccepted: boolean): void => {
    setTimeout(() => {
      this.field.data.value = isAccepted;
      if (this.field.config.onChange) {
        this.logicService.evaluate(this.field.config.onChange, {
          field: this.field,
          page: this.workflowService.currentPage
        });
      }
    }, 10);
  };

  public getDisclosureSelectedStatus(): string {
    if (this.isNullOrEmpty(this.disclosure.accepted) && !this.allApplicantsAccepted(this.disclosure.applicants) && this.isPageSubmitted) {
      return 'cu-warning-solid bl-font-color--error';
    }
    return !this.isNullOrEmpty(this.disclosure.accepted) || this.allApplicantsAccepted(this.disclosure.applicants)
      ? 'cu-check-circle-solid  bl-font-color--success'
      : 'cu-check-circle-solid bl-font-color--silver';
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public updateCrossSellAcceptance(index: number, disclosureRef: any) {
    const applicant = this.disclosure.applicants[index];
    applicant.disabled = true;

    if (applicant.agreedDateStorageId) {
      this.patchValue(applicant.agreedDateStorageId, new Date().toISOString(), index);
    }

    const converetedValue = typeof applicant.accepted == 'string' ? /true/i.test(applicant.accepted) : applicant.accepted;
    if (applicant.storageId) {
      this.patchValue(applicant.storageId, converetedValue, index);
    }

    if (applicant.acceptTextStorageId) {
      this.patchValue(applicant.acceptTextStorageId, applicant.label, index);
    }

    this.afterAgreeChecked(applicant, index);
    this.collapseWhenAllApplicantAccepted(disclosureRef);
    setTimeout(() => {
      applicant.disabled = false;
    }, 10);

    if (index === 0 && this.disclosure.applicants?.length > 1) {
      this.disclosure.applicants[1].accepted = null;
      this.disclosure.applicants[1].readOnly = !converetedValue;
      this.updateCrossSellAcceptance(1, disclosureRef);
    }
  }

  private patchValue(storageId: string, value: PrimitiveType, iterationID: number = undefined): void {
    setTimeout(() => {
      if (storageId.includes('/$/')) {
        this.statefulService.wrap(storageId, iterationID).value = value;
        return;
      }

      this.statefulService.wrap(storageId).value = value;
    }, 1000);
  }

  private afterAgreeChecked(disclosure, index) {
    if (disclosure.storageId) {
      this.statefulService.wrap(disclosure.storageId, index).load(() => {
        this.updateValidity();
      });
    } else {
      this.updateValidity();
    }
  }

  public collapseWhenAllApplicantAccepted(disclosureRef): void {
    const isAccepted = this.disclosure.applicants.filter((t) => this.isNullOrEmpty(t.accepted)).length === 0;
    if (isAccepted) {
      disclosureRef?.close();
    } else {
      disclosureRef?.open();
    }
  }

  public disclosureTitleHtml(): string {
    return `<h3 class="bl-display--flex bl-align-items--flex-start bl-width--100 bl-margin-bottom--0 " aria-hidden="true">
      <i class="bl-font-size--24 bl-margin-right--16 ${this.getDisclosureSelectedStatus()}"></i>
      <span class="bl-text-style--display-5 bl-text-align--left bl-flex--auto">
        <span class="bl-display--block">${this.disclosure.title}</span>
        <span class="bl-text-style--secondary-text-1"> ${this.requiredCompleted(this.disclosure)} </span>
      </span>
      <i class="bl-font-size--32 ${this.disclosure.isOpen ? 'cu-angle-up' : 'cu-angle-down'}"></i>
    </h3>`;
  }

  public requiredCompleted(disclosure: Disclosure): string {
    if (this.isNullOrEmpty(disclosure?.accepted) && !this.allApplicantsAccepted(disclosure?.applicants) && this.isPageSubmitted) {
      return `<span class="bl-font-color--error"> Required </span>`;
    }
    if (!this.isNullOrEmpty(disclosure.accepted) || this.allApplicantsAccepted(disclosure?.applicants)) {
      return `<span class="bl-font-color--success"> Completed </span>`;
    }
    return '';
  }

  public getApplicantLabel(label: string): string {
    const parseToken = '#{';
    const parsedText = this.stringVariableService.parse(label, this.field.iterationId, this.field.pageIterationId, '#{')?.toString();
    if (parsedText.includes(parseToken)) {
      const left = label.indexOf(parseToken);
      const right = label.lastIndexOf('}');
      const searchStr = label.substring(left, right + 1);
      return label.replace(searchStr, 'Applicant');
    }
    return parsedText;
  }
}
